import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  MenuItem,
  IconButton,
  Chip
} from "@mui/material";

import CameraAltIcon from "@mui/icons-material/CameraAlt";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";

import { db, storage } from "../firebase";
import { getDoc, doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";

const colors = {
  darkGreen: "#06362D",
  calPolyGreen: "#155332",
  vanilla: "#DEE2B1",
  gold: "#FFD500",
  goldenrod: "#D1A505",
};

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi", "Jammu and Kashmir",
  "Ladakh", "Puducherry", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Lakshadweep", "Andaman and Nicobar Islands"
];

const PROPERTY_TYPES = ["Hotel", "Bhaktniwas", "Lodge", "Resort", "Villa"];

// Reusable styling for TextFields on dark backgrounds
const darkTextFieldSx = {
  my: 1,
  "& label": { color: colors.vanilla, opacity: 0.8 },
  "& label.Mui-focused": { color: colors.gold },
  "& .MuiInputBase-input": { color: colors.vanilla },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(6, 54, 45, 0.4)",
    "& fieldset": { borderColor: "rgba(222, 226, 177, 0.3)" },
    "&:hover fieldset": { borderColor: colors.goldenrod },
    "&.Mui-focused fieldset": { borderColor: colors.gold },
    "&.Mui-disabled fieldset": { borderColor: "rgba(222, 226, 177, 0.15)" },
  },
  "& .MuiSelect-icon": { color: colors.vanilla },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "rgba(222, 226, 177, 0.5)",
  },
};

function Property() {
  const [propertyId, setPropertyId] = useState(null);
  const [property, setProperty] = useState({});
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingImage, setLoadingImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "info" });

  /* ---------------- LOAD PROPERTY ---------------- */
  useEffect(() => {
    const loadProperty = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setLoading(false);
          return;
        }

        const roleSnap = await getDoc(doc(db, "authroles", user.uid));
        if (!roleSnap.exists()) {
          setLoading(false);
          return;
        }

        const pid = roleSnap.data().propertyId;
        setPropertyId(pid);

        const propSnap = await getDoc(doc(db, "properties", pid));
        if (propSnap.exists()) {
          const data = propSnap.data();
          setProperty(data);
          setEditData({ ...data, country: "India" });
        }
      } catch (err) {
        console.error("Error fetching property:", err);
      } finally {
        setLoading(false);
      }
    };

    loadProperty();
  }, []);

  /* ---------------- IMAGE UPLOAD ---------------- */
  const uploadImage = async (file, field) => {
    if (!file || !propertyId) return;

    try {
      setLoadingImage(true);
      const storageRef = ref(storage, `property/${propertyId}/${field}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const finalUrl = `${url}?t=${Date.now()}`;

      await updateDoc(doc(db, "properties", propertyId), {
        [field]: finalUrl,
      });

      setProperty((prev) => ({ ...prev, [field]: finalUrl }));
      setEditData((prev) => ({ ...prev, [field]: finalUrl }));
      setMessage({ text: "Image updated successfully!", type: "success" });
    } catch (err) {
      console.error(err);
      setMessage({ text: "Image upload failed. Please try again.", type: "error" });
    } finally {
      setLoadingImage(false);
    }
  };

  /* ---------------- SAVE DETAILS ---------------- */
  const saveDetails = async () => {
    try {
      setSaving(true);
      await updateDoc(doc(db, "properties", propertyId), editData);
      setProperty(editData);
      setMessage({ text: "Profile updated successfully!", type: "success" });
      setOpenEdit(false);
    } catch (err) {
      console.error(err);
      setMessage({ text: "Failed to update profile.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colors.darkGreen,
        }}
      >
        <CircularProgress sx={{ color: colors.gold }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: colors.darkGreen,
        p: { xs: 2, md: 4 },
        color: colors.vanilla,
      }}
    >
      {/* Notifications */}
      {message.text && (
        <Alert
          severity={message.type}
          onClose={() => setMessage({ text: "", type: "info" })}
          sx={{
            mb: 3,
            bgcolor: colors.calPolyGreen,
            color: colors.vanilla,
            border: `1px solid ${colors.goldenrod}`,
            "& .MuiAlert-icon": { color: colors.gold },
          }}
        >
          {message.text}
        </Alert>
      )}

      {/* Loading Overlay */}
      {(loadingImage || saving) && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(6, 54, 45, 0.75)",
            backdropFilter: "blur(4px)",
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={60} sx={{ color: colors.gold }} />
          <Typography variant="body1" sx={{ color: colors.vanilla, fontWeight: 500 }}>
            {saving ? "Saving Changes..." : "Uploading Image..."}
          </Typography>
        </Box>
      )}

      {/* COVER IMAGE */}
      <Box
        sx={{
          height: { xs: 220, md: 360 },
          position: "relative",
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 8px 30px rgba(0,0,0,0.45)",
          border: `1px solid rgba(222, 226, 177, 0.15)`,
          backgroundColor: colors.calPolyGreen,
        }}
      >
        <img
          src={property.buildingImage || "https://via.placeholder.com/1200x400/06362D/DEE2B1?text=No+Cover+Image"}
          alt="Cover"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />

        <Button
          component="label"
          startIcon={<CameraAltIcon />}
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            bgcolor: colors.gold,
            color: colors.darkGreen,
            fontWeight: 600,
            textTransform: "none",
            borderRadius: 2,
            px: 2,
            boxShadow: 3,
            "&:hover": { bgcolor: colors.goldenrod, color: "#fff" },
          }}
        >
          Change Cover
          <input
            hidden
            type="file"
            accept="image/*"
            onChange={(e) => uploadImage(e.target.files[0], "buildingImage")}
          />
        </Button>
      </Box>

      {/* PROFILE CARD */}
      <Paper
        elevation={6}
        sx={{
          width: { xs: "95%", md: "75%" },
          mx: "auto",
          mt: { xs: -8, md: -10 },
          p: { xs: 3, md: 5 },
          borderRadius: 4,
          bgcolor: colors.calPolyGreen,
          border: `1px solid rgba(209, 165, 5, 0.35)`,
          color: colors.vanilla,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* LOGO AVATAR */}
        <Box sx={{ position: "relative", width: 130, mx: "auto", mt: -9 }}>
          <IconButton component="label" sx={{ p: 0 }}>
            <Avatar
              src={property.logo || ""}
              sx={{
                width: 130,
                height: 130,
                bgcolor: colors.darkGreen,
                border: `4px solid ${colors.gold}`,
                boxShadow: "0 4px 14px rgba(0,0,0,0.5)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: 4,
                right: 4,
                bgcolor: colors.gold,
                borderRadius: "50%",
                p: "6px",
                display: "flex",
                boxShadow: 2,
                "&:hover": { bgcolor: colors.goldenrod },
              }}
            >
              <CameraAltIcon sx={{ color: colors.darkGreen, fontSize: 20 }} />
            </Box>
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={(e) => uploadImage(e.target.files[0], "logo")}
            />
          </IconButton>
        </Box>

        {/* PROPERTY INFO */}
        <Box sx={{ textAlign: "center", mt: 2.5 }}>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 1 }}>
            {property.type && (
              <Chip
                label={property.type}
                size="small"
                sx={{
                  bgcolor: "rgba(222, 226, 177, 0.15)",
                  color: colors.gold,
                  border: `1px solid ${colors.goldenrod}`,
                  fontWeight: 600,
                }}
              />
            )}
          </Box>

          <Typography variant="h4" fontWeight={700} sx={{ color: colors.vanilla, mb: 1 }}>
            {property.hotelname || "Unnamed Property"}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              color: "rgba(222, 226, 177, 0.85)",
              mb: 0.5,
            }}
          >
            <LocationOnIcon fontSize="small" sx={{ color: colors.gold }} />
            <Typography variant="body1">
              {[property.address, property.city, property.state, property.pincode]
                .filter(Boolean)
                .join(", ") || "No address provided"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 3,
              mt: 1.5,
              color: colors.vanilla,
            }}
          >
            {property.mobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <PhoneIcon fontSize="small" sx={{ color: colors.gold }} />
                <Typography variant="body2">{property.mobile}</Typography>
              </Box>
            )}
            {property.email && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <EmailIcon fontSize="small" sx={{ color: colors.gold }} />
                <Typography variant="body2">{property.email}</Typography>
              </Box>
            )}
          </Box>

          {property.gstNo && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.8,
                mt: 1.5,
                px: 1.5,
                py: 0.4,
                borderRadius: 1.5,
                bgcolor: "rgba(6, 54, 45, 0.5)",
                border: "1px dashed rgba(222, 226, 177, 0.3)",
              }}
            >
              <BadgeIcon fontSize="small" sx={{ color: colors.goldenrod }} />
              <Typography variant="body2" sx={{ color: colors.vanilla }}>
                GST No: <strong>{property.gstNo}</strong>
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <Button
              startIcon={<EditIcon />}
              variant="contained"
              onClick={() => setOpenEdit(true)}
              sx={{
                bgcolor: colors.gold,
                color: colors.darkGreen,
                fontWeight: 700,
                px: 3,
                textTransform: "none",
                borderRadius: 2,
                "&:hover": { bgcolor: colors.goldenrod, color: "#fff" },
              }}
            >
              Edit Profile
            </Button>
          </Box>
        </Box>

        {/* OFFICIAL STAMP SECTION */}
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: "1px solid rgba(222, 226, 177, 0.2)",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: colors.gold }}>
            Official Property Stamp
          </Typography>

          <Box sx={{ maxWidth: 220, mx: "auto" }}>
            <Box
              sx={{
                height: 110,
                border: `2px dashed ${colors.goldenrod}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1.5,
                borderRadius: 2,
                bgcolor: "rgba(6, 54, 45, 0.6)",
                p: 1,
              }}
            >
              {property.stamp ? (
                <img
                  src={property.stamp}
                  alt="Stamp"
                  style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                />
              ) : (
                <Typography variant="caption" sx={{ color: "rgba(222, 226, 177, 0.6)" }}>
                  No Stamp Uploaded
                </Typography>
              )}
            </Box>

            <Button
              component="label"
              size="small"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              sx={{
                borderColor: colors.gold,
                color: colors.gold,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: colors.goldenrod,
                  bgcolor: "rgba(255, 213, 0, 0.08)",
                },
              }}
            >
              Upload Stamp
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => uploadImage(e.target.files[0], "stamp")}
              />
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* EDIT DIALOG */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: colors.calPolyGreen,
            color: colors.vanilla,
            borderRadius: 3,
            border: `1px solid ${colors.goldenrod}`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.gold, fontWeight: 700, pb: 1 }}>
          Edit Property Details
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: "rgba(222, 226, 177, 0.15)" }}>
          <TextField
            label="Hotel Name"
            fullWidth
            size="small"
            sx={darkTextFieldSx}
            value={editData.hotelname || ""}
            onChange={(e) => setEditData({ ...editData, hotelname: e.target.value })}
          />

          <TextField
            label="Email"
            fullWidth
            size="small"
            sx={darkTextFieldSx}
            value={editData.email || ""}
            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
          />

          <TextField
            label="Mobile"
            fullWidth
            size="small"
            sx={darkTextFieldSx}
            value={editData.mobile || ""}
            onChange={(e) => setEditData({ ...editData, mobile: e.target.value })}
          />

          <TextField
            label="GST Number"
            fullWidth
            size="small"
            sx={darkTextFieldSx}
            value={editData.gstNo || ""}
            onChange={(e) => setEditData({ ...editData, gstNo: e.target.value })}
          />

          <TextField
            label="Address"
            fullWidth
            size="small"
            sx={darkTextFieldSx}
            value={editData.address || ""}
            onChange={(e) => setEditData({ ...editData, address: e.target.value })}
          />

          <TextField
            label="City"
            fullWidth
            size="small"
            sx={darkTextFieldSx}
            value={editData.city || ""}
            onChange={(e) => setEditData({ ...editData, city: e.target.value })}
          />

          <TextField
            select
            label="State"
            fullWidth
            size="small"
            sx={darkTextFieldSx}
            value={editData.state || ""}
            onChange={(e) => setEditData({ ...editData, state: e.target.value })}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    bgcolor: colors.darkGreen,
                    color: colors.vanilla,
                    "& .MuiMenuItem-root:hover": { bgcolor: colors.calPolyGreen },
                    "& .Mui-selected": { bgcolor: `${colors.calPolyGreen} !important` },
                  },
                },
              },
            }}
          >
            {INDIAN_STATES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Country"
            fullWidth
            size="small"
            sx={darkTextFieldSx}
            value="India"
            disabled
          />

          <TextField
            label="Pincode"
            fullWidth
            size="small"
            sx={darkTextFieldSx}
            value={editData.pincode || ""}
            onChange={(e) => setEditData({ ...editData, pincode: e.target.value })}
          />

          <TextField
            select
            label="Property Type"
            fullWidth
            size="small"
            sx={darkTextFieldSx}
            value={editData.type || ""}
            onChange={(e) => setEditData({ ...editData, type: e.target.value })}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    bgcolor: colors.darkGreen,
                    color: colors.vanilla,
                    "& .MuiMenuItem-root:hover": { bgcolor: colors.calPolyGreen },
                    "& .Mui-selected": { bgcolor: `${colors.calPolyGreen} !important` },
                  },
                },
              },
            }}
          >
            {PROPERTY_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setOpenEdit(false)}
            sx={{ color: colors.vanilla, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveDetails}
            sx={{
              bgcolor: colors.gold,
              color: colors.darkGreen,
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { bgcolor: colors.goldenrod, color: "#fff" },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Property;